import { Fragment } from 'react';
import { Link, useMatches } from '@tanstack/react-router';

import type { Crumb } from './personalTrail';
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Breadcrumb as BreadcrumbUI,
} from '@/components/ui/breadcrumb';

interface BreadcrumbProps {
  /** Explicit trail override; falls back to the route-context trail. */
  trail?: Crumb[];
}

/**
 * Renders a breadcrumb trail. By default this is the trail attached to the
 * deepest matched route via its `beforeLoad` context (deepest match wins
 * because child routes typically extend the parent trail). The personal
 * edition passes an adapted trail explicitly via the `trail` prop.
 */
export function Breadcrumb({ trail: trailProp }: BreadcrumbProps = {}) {
  const routeTrail = useBreadcrumbTrail();
  const trail = trailProp ?? routeTrail;

  if (!trail || trail.length === 0) {
    return null;
  }

  return (
    <BreadcrumbUI>
      <BreadcrumbList>
        {trail.map((crumb, index) => {
          const isLast = index === trail.length - 1;
          return (
            <Fragment key={crumb.href ?? crumb.title}>
              <BreadcrumbItem>
                {isLast || !crumb.href ? (
                  <BreadcrumbPage className="font-medium">{crumb.title}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.href}>{crumb.title}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </BreadcrumbUI>
  );
}

function useBreadcrumbTrail(): Crumb[] | undefined {
  const matches = useMatches();
  // Walk matches deepest-first without mutating the array the router gave us.
  for (let i = matches.length - 1; i >= 0; i--) {
    const ctx = matches[i].context as { breadcrumbs?: Crumb[] } | undefined;
    if (ctx?.breadcrumbs) return ctx.breadcrumbs;
  }
  return undefined;
}
